import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, Tab, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { API_URL, gridSpacing } from 'utils/constants';
import ApiHelper from 'utils/apiHelper';
import MainCard from 'components/Cards/MainCard';
import AddRuleDialog from './AddRuleDialog';
import EditRuleDialog from './EditRuleDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';
import DeliveryReportRule from './DeliveryReportRule';
import AddReportDeliveryDialog from './AddReportDeliveryDialog';
import AddEnergyRuleDialog from './AddEnergyRuleDialog';
import { getOperatorLookups } from './ruleTypeData';
import { richTreeViewClasses } from '@mui/x-tree-view';

const Automation = () => {
    const theme = useTheme();
    const { userInfo } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const manageOrganizationData = useSelector((state) => state.management.organization?.selected);
    const [deletionRuleId, setDeletionRuleId] = useState();
    const [value, setValue] = useState('1');
    const [ruleType, setRuleType] = useState('group');
    const [groupRulesData, setGroupRulesData] = useState([]);
    const [policyRulesData, setPolicyRulesData] = useState([]);
    const [licenseRulesData, setLicenseRulesData] = useState([]);
    const [energyRulesData, setEnergyRulesData] = useState([]);
    const [commonData, setCommonData] = useState({});
    const [ruleStatementValues, setRuleStatementValues] = useState({});
    const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
    const [showAddReportDialog, setShowAddReportDialog] = useState(false);
    const [showAddEnergyRuleDialog, setShowAddEnergyRuleDialog] = useState(false);
    const [showEditRuleDialog, setShowEditRuleDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [selectedRuleData, setSelectedRuleData] = useState({});
    const [selectedRuleStatements, setSelectedRuleStatements] = useState([]);
    const [DeliveryList, setDeliveryList] = useState({});
    const [inputFields, setInputFields] = useState({});

    useEffect(() => {
        getRuleStatementValuesData();
        getRuleStatementsData();
        // getRulesData();
    }, [DeliveryList]);

    const initStatement = useCallback(
        (ruleType, statement) => {
            if (ruleType === 'group') {
                statement['device_attrib'] = commonData.group.attributes.find((a) => a.attribname === statement.deviceattrib);
            } else if (ruleType === 'policy') {
                statement['device_attrib'] = commonData.policy.attributes.find((a) => a.attribname === statement.deviceattrib);
            } else if (ruleType === 'license') {
                statement['device_attrib'] = commonData.license.attributes.find((a) => a.attribname === statement.deviceattrib);
            } else {
                statement['device_attrib'] = commonData.rates.attributes.find((a) => a.attribname === statement.deviceattrib);
            }
            statement['connector'] = commonData.connectors.find((c) => c.connectorid === statement.connector_id);
            statement['operator'] = commonData.operators.find((o) => o.operatorid === statement.operator_id);
            if (statement.deviceattrib === 'groupid') {
                statement.value = ruleStatementValues.group_values.find((gv) => `${gv.id}` === `${statement.value}`)?.name;
            } else if (statement.deviceattrib === 'devtype') {
                statement.value = ruleStatementValues.device_type_values.find((gv) => `${gv.devtypeid}` === `${statement.value}`)?.devtype;
            } else if (statement.deviceattrib === 'viewid') {
                statement.value = ruleStatementValues.view_values.find((gv) => `${gv.id}` === `${statement.value}`)?.name;
            } else {
                if (statement.value === '1' || statement.value === 'Yes') {
                    statement.value = 'Yes';
                } else if (statement.value === '0' || statement.value === 'No') {
                    statement.value = 'No';
                }
            }
            return statement;
        },
        [commonData, ruleStatementValues]
    );

    const getRulesData = useCallback(() => {
        setLoading(true);
        ApiHelper.get('/api/automation/rules/')
            .then((res) => {
                const populatedGroupRules = (res.data.group_rules || []).map((groupRule) => ({
                    ...groupRule,
                    rule_statements: (groupRule.rule_statements || []).map((statement) => initStatement('group', statement))
                }));

                const populatedPolicyRules = (res.data.policy_rules || []).map((policyRule) => ({
                    ...policyRule,
                    rule_statements: (policyRule.rule_statements || []).map((statement) => initStatement('policy', statement))
                }));

                const populatedLicenseRules = (res.data.license_rules || []).map((licenseRule) => ({
                    ...licenseRule,
                    rule_statements: (licenseRule.rule_statements || []).map((statement) => initStatement('license', statement))
                }));

                const populatedEnergyRules = (res.data.energy_rules || []).map((energyRule) => ({
                    ...energyRule,
                    rule_statements: (energyRule.rule_statements || []).map((statement) => initStatement('energy', statement))
                }));

                setGroupRulesData(populatedGroupRules);
                setPolicyRulesData(populatedPolicyRules);
                setLicenseRulesData(populatedLicenseRules);
                setEnergyRulesData(populatedEnergyRules);

                setLoading(false);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the rules data');
                    setLoading(false);
                }
            });
    }, [initStatement]);

    useEffect(() => {
        if (commonData.hasOwnProperty('group') && commonData.group) {
            getRulesData();
        }
    }, [commonData, getRulesData]);

    const getRuleStatementsData = () => {
        const getOperators = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'operators' });
        const getConnectors = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'connectors' });
        const getGroup = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'group' });
        const getPolicy = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'policy' });
        const getEnergy = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'energy' });
        const getLicense = ApiHelper.get(API_URL.RULE_STATEMENT_DATA, {}, {}, { type: 'license' });
        Promise.all([getOperators, getConnectors, getGroup, getPolicy, getEnergy, getLicense])
            .then(([resOperators, resConnectors, resGroup, resPolicy, resRates, resLicense]) => {
                setCommonData({
                    operators: resOperators.data,
                    connectors: resConnectors.data,
                    group: resGroup.data,
                    policy: resPolicy.data,
                    rates: resRates.data,
                    license: resLicense.data
                });
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get rule statements data');
                }
            });
    };

    const getRuleStatementValuesData = () => {
        ApiHelper.get('/api/automation/rule_statement_values/')
            .then((res) => {
                setRuleStatementValues(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get rule statement values data');
                }
            });
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
        if (newValue === '1') {
            setRuleType('group');
        } else if (newValue === '2') {
            setRuleType('policy');
        } else if (newValue === '2') {
            setRuleType('policy');
        } else if (newValue === '3') {
            setRuleType('alert_rule');
        } else if (newValue === '4') {
            setRuleType('report_delivery');
        } else if (newValue === '6') {
            setRuleType('license');
        } else {
            setRuleType('energy_group');
        }
    };

    const handleRuleStatements = (data) => {
        let rule_statements = '';
        if (data.rule_statements.length > 0) {
            data.rule_statements.map((statement) => {
                // device_attrib / operator are resolved by initStatement() via
                // .find(), which yields undefined when a statement references
                // something missing from this rule type's lookup list. Falling
                // back to the raw stored value keeps one bad statement from
                // taking the whole Automation page down with a TypeError.
                const attribLabel = statement.device_attrib?.attrib_label ?? statement.deviceattrib ?? '(unknown attribute)';
                const operator = statement.operator?.name ?? '(unknown operator)';
                const value = `'${statement.value}'`;
                const connector = statement.connector?.name ? ` ${statement.connector.name} ` : ' ';
                let formattedOperator;

                if (statement.negative === 'True') {
                    switch (operator) {
                        case 'equals':
                            formattedOperator = 'does not equal';
                            break;
                        case 'contains':
                            formattedOperator = 'does not contain';
                            break;
                        case 'starts with':
                            formattedOperator = 'does not start with';
                            break;
                        case 'ends with':
                            formattedOperator = 'does not end with';
                            break;
                        case 'is greater than':
                            formattedOperator = 'is not greater than';
                            break;
                        case 'is less than':
                            formattedOperator = 'is not less than';
                            break;
                        case 'is greater than or equal to':
                            formattedOperator = 'is not greater than or equal to';
                            break;
                        case 'is less than or equal to':
                            formattedOperator = 'is not less than or equal to';
                            break;
                        default:
                            formattedOperator = operator;
                    }
                } else {
                    formattedOperator = operator;
                }

                rule_statements += `${attribLabel} ${formattedOperator} ${value}${connector}`;
            });
        }
        return rule_statements.trim();
    };

    const handleClose = () => {
        if (showAddRuleDialog) setShowAddRuleDialog(false);
        else if (showEditRuleDialog) setShowEditRuleDialog(false);
        else if (showAddEnergyRuleDialog) setShowAddEnergyRuleDialog(false);
        else setShowDeleteConfirmationDialog(false);
        getRuleStatementsData();
        // getRulesData();
    };

    const handleCloseReportDelivery = () => {
        window.location.reload();
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeletionRuleId(id);
    };

    const handleCloseEnergyRule = () => {
        return;
    };

    const handleDeleteRule = () => {
        ApiHelper.delete(`/api/automation/rules/${deletionRuleId}/`)
            .then(() => {
                toast.success('Successfully deleted the rule.');
                setShowDeleteConfirmationDialog(false);
                getRuleStatementsData();
                // getRulesData();
            })
            .catch(() => {
                toast.error('Failed to delete the rule.');
            });
    };

    const handleEditRule = (data) => {
        let tmp = [];
        let groupAttributeId = 0;
        let deviceTypeAttributeId = 0;
        let viewAttributeId = 0;
        if (data.rule_statements?.length <= 0) {
            toast.warning('No rule statement data');
            return;
        }
        const operatorLookupsData = getOperatorLookups(commonData, ruleType);
        operatorLookupsData.map((operatorLookupData) => {
            if (operatorLookupData.attrib.attribname === 'groupid') {
                groupAttributeId = operatorLookupData.attrib.attribid;
            }
            if (operatorLookupData.attrib.attribname === 'devtype') {
                deviceTypeAttributeId = operatorLookupData.attrib.attribid;
            }
            if (operatorLookupData.attrib.attribname === 'viewid') {
                viewAttributeId = operatorLookupData.attrib.attribid;
            }
        });
        data.rule_statements?.map((ruleStatementData) => {
            const operatorLookup = operatorLookupsData.some((item) => item.attrib.attribid === ruleStatementData.device_attrib.attribid);
            let valueOptions = [];
            let valueTextField = true;
            let operatorOptions = commonData.operators;
            if (operatorLookup) {
                operatorOptions = [operatorLookupsData[0].theoper];
                if (ruleStatementData.device_attrib.attribid === groupAttributeId) valueOptions = ruleStatementValues.group_values;
                else if (ruleStatementData.device_attrib.attribid === deviceTypeAttributeId)
                    valueOptions = ruleStatementValues.device_type_values;
                else if (ruleStatementData.device_attrib.attribid === viewAttributeId) valueOptions = ruleStatementValues.view_values;
                else valueOptions = [{ name: 'Yes' }, { name: 'No' }];
                valueTextField = false;
            }
            tmp.push({
                id: ruleStatementData.statmentid,
                attribute: ruleStatementData.device_attrib.attribid,
                negative: ruleStatementData.negative,
                operator: ruleStatementData.operator.operatorid,
                value: ruleStatementData.value,
                connector: ruleStatementData.connector ? ruleStatementData.connector.connectorid : '1',
                disabledConnector: !ruleStatementData.connector?.connectorid,
                operatorOptions: operatorOptions,
                valueTextField: valueTextField,
                valueOptions: valueOptions
            });
        });
        setSelectedRuleStatements(tmp);
        setShowEditRuleDialog(true);
        setSelectedRuleData(data);
    };

    if (!groupRulesData || !policyRulesData) {
        return null;
    } else {
        return (
            <MainCard title="Automation" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>
                                    {manageOrganizationData?.orgid ? manageOrganizationData?.name : userInfo.user.orgid.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                {loading === false && (ruleType === 'group' || ruleType === 'policy') && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddRuleDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Rule
                                    </Button>
                                )}
                                {ruleType === 'license' && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddRuleDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Rule
                                    </Button>
                                )}
                                {ruleType === 'report_delivery' && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddReportDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Report Delivery
                                    </Button>
                                )}
                                {ruleType === 'energy_group' && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddEnergyRuleDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Energy Rule
                                    </Button>
                                )}
                                {showAddRuleDialog && (
                                    <AddRuleDialog
                                        ruleType={ruleType}
                                        data={commonData}
                                        onClose={handleClose}
                                        ruleStatementValues={ruleStatementValues}
                                        onCloseCancel={() => setShowAddRuleDialog(false)}
                                    />
                                )}
                                {showAddReportDialog && (
                                    <AddReportDeliveryDialog
                                        onClose={handleCloseReportDelivery}
                                        onCloseCancel={() => setShowAddReportDialog(false)}
                                        setData={setDeliveryList}
                                    />
                                )}
                                {showAddEnergyRuleDialog && (
                                    <AddEnergyRuleDialog
                                        ruleType={ruleType}
                                        data={commonData}
                                        onClose={handleClose}
                                        ruleStatementValues={ruleStatementValues}
                                        onCloseCancel={() => setShowAddEnergyRuleDialog(false)}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Box sx={{ width: '100%', typography: 'body1' }}>
                            <TabContext value={value}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <TabList onChange={handleChange} indicatorColor="secondary">
                                        <Tab label="Group Rules" value="1" />
                                        <Tab label="Policy Rules" value="2" />
                                        <Tab label="License Rules" value="6" />
                                        {/*<Tab label="Alert Rules" value="3" />*/}
                                        {userInfo.admin_permissions.root_admin && <Tab label="Energy Rate Rules" value="5" />}
                                        <Tab label="Report Delivery" value="4" />
                                    </TabList>
                                </Box>
                                <TabPanel value="1">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : groupRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No Group Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>Group</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groupRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>
                                                                {data.enabled === true || data.enabled === 1 ? 'YES' : 'NO'}
                                                            </TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.group_name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box style={{ display: 'flex' }}>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="group rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                                <TabPanel value="2">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : policyRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No Policy Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>Policy</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {policyRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>
                                                                {data.enabled === true || data.enabled === 1 ? 'YES' : 'NO'}
                                                            </TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.policy_name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="policy rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                                <TabPanel value="3">
                                    <Typography>This tab will allow for creating/editing rules about when to alert users.</Typography>
                                </TabPanel>
                                <TabPanel value="4">
                                    <DeliveryReportRule data={DeliveryList} />
                                </TabPanel>
                                <TabPanel value="5">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : energyRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No Energy Rate Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>Rate Name</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {energyRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>
                                                                {data.enabled === true || data.enabled === 1 ? 'YES' : 'NO'}
                                                            </TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.rate_default ? 'Default' : data.rate_name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="policy rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                                <TabPanel value="6">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : licenseRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No License Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>License</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {licenseRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>
                                                                {data.enabled === true || data.enabled === 1 ? 'YES' : 'NO'}
                                                            </TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.license_name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="license rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                            </TabContext>
                        </Box>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default Automation;
